<script setup lang="ts">
import type { BasePageProps } from '~/models/Presentation/BasePage'
import type { AudioPage } from '~/models/Presentation/Pages/AudioPage'
import 'vidstack/bundle'
import PresentationRichText from '~/components/PresentationPages/Components/PresentationRichText.vue'
import { useQuery } from '@tanstack/vue-query'
import useContentApi from '~/api/contentApi'
import AudioPlayer from '../media/AudioPlayer.vue'
import { ContentType } from '~/models/Content/ContentType'
import { Subtree } from '~/models/Content/Subtree'
import { computed, onMounted, ref } from 'vue'
import { KsIcon } from '@aschehoug/kloss'
import { ContentAudio } from '~/models/Content/ContentAudio'
import useImage from '~/composables/useImage'
import { rdfRestClient } from '~/api/client/rdfRestClient'
import { useLottie } from '~/composables/useLottie'
import TimeSlider from '../media/TimeSlider.vue'

const props = defineProps<BasePageProps & { page: AudioPage }>()

const { findContents } = useContentApi()
const { getImageSrc, getSvgSrc, getImageAltText } = useImage()
const { lottieJSON, getLottie } = useLottie()
const { data } = useQuery({
  queryKey: ['audio-page', props.page.audio.destinationContentId, props.currentPageNumber],
  queryFn: async () => {
    const [content] = await findContents<ContentAudio>({
      subtreeCriterion: [Subtree.Content, Subtree.Media],
      contentTypeCriterion: [ContentType.Audio],
      contentIdCriterion: [Number(props.page.audio.destinationContentId)],
    })

    const { data } = await rdfRestClient.get(`/resources/contentItems/${content.metadata?.contentItemId}`)
    const title: string | undefined = data
      ?.results
      ?.bindings
      ?.find(({ element }: any) => element?.value === import.meta.env.VITE_RDF_REST_PROP_TITLE)
      ?.litteral
      ?.value

    return { content, title }
  },
  staleTime: Infinity,
})


const lottieId = computed( () => Number(props.page.lottie?.destinationContentId))
const imageSrc = computed(() => {
  if (props.page.svg?.destinationContentId) return getSvgSrc(props.page.svg)
  if (props.page.image?.destinationContentId) return getImageSrc(props.page.image)
  return ''
})
const imageAltText = computed(() => props.page.image ? getImageAltText(props.page.image) : '')
const showTranscript = ref(false)

onMounted(() => lottieId.value && getLottie(lottieId.value))
</script>

<template>
  <div
    class="audio-slide-grid h-full font-inter text-white"
    :style="{ background: page.colorPair.background }"
  >
    <LottieAnimation
      v-if="lottieJSON"
      :animation-data="lottieJSON"
      width="100%"
    />
    <img
      v-else
      class="w-full self-stretch object-cover"
      style="grid-area: img"
      :src="imageSrc"
      :alt="imageAltText"
    >

    <div
      :class="page.colorPair.isDark ? 'bg-black/20' : 'bg-white/20'"
      style="grid-area: audio / img"
    />

    <div
      class="max-h-full min-h-0 self-center overflow-y-auto px-16 lg:px-36 2xl:px-48 py-8"
      :class="page.colorPair.isDark ? 'text-white' : 'text-black'"
      style="grid-area: text;"
    >
      <template v-if="showTranscript">
        <h2 :class="[page.textSizes.paragraph, 'mb-4 font-bold']">((Transcription title))</h2>
        <div :class="[page.textSizes.paragraph]">((Transcription body))</div>
      </template>

      <template v-else>
        <h1
          class="text-pretty"
          :class="[page.textSizes.heading, page.fontWeight, page.fontFamily]"
          :style="{ color: page.colorPair.text }"
        >
          {{ page.header || page.title }}
        </h1>
        <PresentationRichText
          v-if="page.body"
          :page="page"
        />
      </template>
    </div>

    <div
      class="relative flex items-center"
      :class="page.colorPair.isDark ? 'bg-black/20 text-white' : 'bg-white/20'"
      style="grid-area: audio"
      :style="{ color: !page.colorPair.isDark && page.colorPair.text }"
    >
      <AudioPlayer
        class="grid gap-4 pl-28 pr-16 text-3xl tabular-nums lg:px-36 2xl:px-48"
        :src="data?.content?.metadata?.elementURI"
        :title.attr="data?.title ?? '\u00A0'"
        :poster="imageSrc"
        inline
      >
        <template #controls>
          <media-play-button
            class="group absolute left-0 grid h-40 w-40 -translate-x-1/2 place-content-center rounded-full border-4 border-[currentColor] bg-white text-5xl shadow-lg hover:bg-white data-[focus]:ring lg:h-52 lg:w-52 lg:text-6xl"
            :style="{ color: page.colorPair.isDark ? page.colorPair.background : page.colorPair.text }"
          >
            <ksIcon
              id="play-solid"
              class="hidden translate-x-[10%] group-data-[paused]:block"
            />
            <ksIcon
              id="pause"
              class=" group-data-[paused]:hidden"
            />
          </media-play-button>

          <div class="flex items-center gap-8 overflow-hidden">
            <media-title class="flex-auto select-text overflow-hidden break-words font-semibold" />
            <!-- Transcription is currently not implemented -->
            <!-- <KsButton
              variant="primary"
              shape="round"
              size="large"
              class="ml-auto !text-[inherit]"
              :style="{
                '--ks-primary': page.colorPair.background,
                '--ks-secondary': page.colorPair.isDark ? '#0002' : '#fff2',
              }"
              @click="showTranscript = !showTranscript"
            >
              <KsIcon id="text" />
            </KsButton> -->
          </div>

          <div class="flex items-center gap-8">
            <media-time
              type="current"
              class="font-medium"
            />

            <TimeSlider
              :style="{
                '--media-color': page.colorPair.isDark ? 'currentColor' : page.colorPair.text,
                '--media-bg-color': page.colorPair.background
              }"
            />

            <media-time
              type="duration"
              class="font-medium"
            />
          </div>
        </template>
      </AudioPlayer>
    </div>
  </div>
</template>

<style scoped>
.audio-slide-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  grid-template-rows: 7fr minmax(16rem, 4fr);
  grid-template-areas:
    'img text'
    'img audio';
}
</style>
